import {
  Component,
  Inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  Input,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { TotalCohortHighSchoolDemographic } from '../../../../../server/types';
import { DataService } from '@/shared/services';

@Component({
  selector: 'students-academic-progress-rate-graph',
  templateUrl: './students-academic-progress-rate-graph.component.html',
  styleUrls: ['./students-academic-progress-rate-graph.component.css'],
  standalone: false,
})
export class StudentsAcademicProgressRateGraphComponent implements OnInit {
  @Input({ required: true }) chartData!: TotalCohortHighSchoolDemographic[];
  @Input({ required: true }) numStudents!: number;

  private root!: am5.Root;
  private chart4!: am5xy.XYChart;

  // data series variable
  private series1!: am5xy.ColumnSeries;
  private series2!: am5xy.ColumnSeries;
  private series3!: am5xy.ColumnSeries;

  // label variable
  private labelSubtitle!: am5.Label;
  private labelTitle!: am5.Label;
  private xAxisLabel!: am5.Label;

  constructor(
    private dataService: DataService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.generateGraph();
  }

  ngOnChanges() {
    this.generateGraph();
  }

  generateGraph() {
    // Create root element
    if (!this.root) this.root = am5.Root.new('chartdiv4');

    // Remove amcharts logo below graphs
    this.root._logo!.dispose();

    // Set themes
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // if chart is not empty then erase it for new data coming
    if (this.chart4) {
      this.root.container.children.pop();
    }

    // Create chart
    this.chart4 = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        layout: this.root.verticalLayout,
      }),
    );

    // Create axes
    let xRenderer = am5xy.AxisRendererX.new(this.root, {
      minGridDistance: 30,
    });
    xRenderer.labels.template.setAll({
      centerY: am5.p50,
      centerX: am5.p50,
      paddingRight: 15,
      paddingTop: 20,
    });

    let xAxis = this.chart4.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: 'label',
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.root, {}),
      }),
    );

    xAxis.data.setAll(this.chartData);

    let yAxis = this.chart4.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(this.root, {}),
      }),
    );

    if (this.series1 || this.series2 || this.series3) {
      this.chart4.series.pop();
    }

    // data labels config
    // const dataLabelConfig = (pctKey: string) => () =>
    //   am5.Bullet.new(this.root, {
    //     locationY: 0,
    //     sprite: am5.Label.new(this.root, {
    //       text: `{valueY}\n{${pctKey}}%`,
    //       fill: am5.color(0xffffff),
    //       centerY: am5.p100,
    //       centerX: am5.p50,
    //       populateText: true,
    //       shadowColor: am5.color(0x000),
    //       shadowBlur: 5,
    //       shadowOffsetX: 2,
    //       shadowOffsetY: 2,
    //     }),
    //   });

    if (
      'total_migrated_apr' in this.chartData[0] &&
      'total_stayed_apr' in this.chartData[0] &&
      'total_non_apr' in this.chartData[0]
    ) {
      // Add series
      // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
      this.series1 = this.chart4.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: 'Total Migrated APR',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'total_migrated_apr',
          categoryXField: 'label',
        }),
      );
      this.series1.data.setAll(this.chartData);
      this.series1.set('fill', am5.color(0x5aaa95));

      // Calculate the percentage for each bar
      this.series1.columns.template.adapters.add(
        'tooltipText',
        (_tooltipText, target) => {
          // Update tooltip with percentage
          return `[bold]{name}[/]\n{categoryX}: {valueY}, {migrated_apr_percentage}%`;
        },
      );

      this.series2 = this.chart4.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: 'Total Stayed APR',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'total_stayed_apr',
          categoryXField: 'label',
        }),
      );
      this.series2.data.setAll(this.chartData);
      this.series2.set('fill', am5.color(0x6794dc));
      // Calculate the percentage for each bar
      this.series2.columns.template.adapters.add(
        'tooltipText',
        (_tooltipText, target) => {
          var valueY = target.dataItem
            ?.dataContext as TotalCohortHighSchoolDemographic;

          // Update tooltip with percentage
          return `[bold]{name}[/]\n{categoryX}: {valueY}, {stayed_apr_percentage}%`;
        },
      );

      this.series3 = this.chart4.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: 'Total Non APR',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'total_non_apr',
          categoryXField: 'label',
        }),
      );
      this.series3.data.setAll(this.chartData);

      // Calculate the percentage for each bar
      this.series3.columns.template.adapters.add(
        'tooltipText',
        (_tooltipText, target) => {
          var valueY = target.dataItem
            ?.dataContext as TotalCohortHighSchoolDemographic;

          // Update tooltip with percentage
          return `[bold]{name}[/]\n{categoryX}: {valueY}, {non_apr_percentage}%`;
        },
      );

      // add chart data value labels
      // this.series1.bullets.push(dataLabelConfig('migrated_apr_percentage'));
      // this.series2.bullets.push(dataLabelConfig('stayed_apr_percentage'));
      // this.series3.bullets.push(dataLabelConfig('non_apr_percentage'));
    } else {
      // Add series
      this.series1 = this.chart4.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: 'Remaining Students APR',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'total',
          categoryXField: 'label',
        }),
      );
      this.series1.data.setAll(this.chartData);
      this.series1.set('fill', am5.color(0xb30000));

      // Calculate the percentage for each bar
      this.series1.columns.template.adapters.add(
        'tooltipText',
        (_tooltipText, target) => {
          var valueY = target.dataItem
            ?.dataContext as TotalCohortHighSchoolDemographic;

          // Update tooltip with percentage
          return `[bold]{name}[/]\n{categoryX}: {valueY}, {percentage}%`;
        },
      );

      // this.series1.bullets.push(dataLabelConfig('percentage'));
    }

    // Add legend
    let legend = this.chart4.children.push(am5.Legend.new(this.root, {}));
    legend.data.setAll(this.chart4.series.values);

    // Make stuff animate on load
    this.chart4.appear(1000, 100);
    this.series1.appear();

    // Add label in Y-axis
    yAxis.axisHeader.children.push(
      am5.Label.new(this.root, {
        text: 'Count of UWF ID (returned 2nd fall)',
        fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
        fontSize: 20,
        fontWeight: '500',
      }),
    );

    // Add titles
    // Title Label
    this.labelTitle = this.chart4.children.unshift(
      am5.Label.new(this.root, {
        // text: 'Enrollment from 1st to 4th Fall',
        fontSize: 20,
        fontFamily: "'Arial', 'Helvetica', 'sans-serif'",
        fontWeight: '900',
        textAlign: 'center',
        x: am5.percent(50),
        y: am5.percent(0),
      }),
    );
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  exportChart() {
    this.dataService.exportAmChartsAsPNG(this.root, 'apr-trends');
  }
}
